import { UploadFile as UploadFileIcon, Warning as WarningIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { preflightFirmwareUpgradeBulk, startFirmwareUpgradeBulk } from '@sophocles/api/firmwareUpgrades';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import StatusChip from '@sophocles/components/StatusChip';
import { selectUnitModels } from '@sophocles/features/reference';
import {
  buildModelNameResolver,
  formatIncompatibleFirmwareModelError,
  formatMixedBatchError,
} from '@sophocles/views/admin/engineering/firmwareUpgrades/firmwareUpgradeHelpers';
import {
  allRiskySelected,
  applyRiskyInclusion,
  countByStatus,
  initialPreflightSelection,
  preflightHasWarnings,
  RISKY_STATUSES,
  SELECTABLE_STATUSES,
  selectionHasBlockedEntry,
} from '@sophocles/views/admin/engineering/firmwareUpgrades/firmwareUpgradePreflight';
import FirmwareVersionAutocomplete from '@sophocles/views/admin/engineering/firmwareUpgrades/FirmwareVersionAutocomplete';

const MAX_ICCIDS = 500;

// '' = Auto: the upgrade_type key is omitted from the request so the
// backend picks the right upgrader per device model. The named options
// force a specific upgrader (DEFAULT is the legacy DefaultUpgrader — not
// automatic despite the old label).
const UPGRADE_TYPES = [
  { value: '', label: 'Auto (recommended)' },
  { value: 'DEFAULT', label: 'DEFAULT' },
  { value: 'NON302M', label: 'NON302M' },
  { value: 'DATAGRAM', label: 'DATAGRAM' },
  { value: '_302_v52', label: '302 v52' },
  { value: 'PERSISTENT', label: 'PERSISTENT' },
];

const splitIccids = raw =>
  raw
    .split(/[\s,;]+/)
    .map(s => s.trim())
    .filter(Boolean);

// Naive CSV parser. Handles commas, semicolons, and quoted cells with
// embedded delimiters; that covers exports from sheets / SIM-management
// portals without dragging in a parser library. If the first row's
// header-detect heuristic fires, we drop it; otherwise every row is
// treated as data.
const parseCsv = text => {
  if (!text) return [];
  const lines = text.split(/\r?\n/).filter(line => line.length > 0);
  if (lines.length === 0) return [];

  const parseLine = line => {
    const cells = [];
    let cell = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          cell += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          cell += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',' || ch === ';' || ch === '\t') {
        cells.push(cell.trim());
        cell = '';
      } else {
        cell += ch;
      }
    }
    cells.push(cell.trim());
    return cells;
  };

  const rows = lines.map(parseLine);

  // Header detection: if any header cell contains "iccid"
  // (case-insensitive), treat row 0 as a header and use the matching
  // column. Otherwise no header, and we read the first column.
  const header = rows[0];
  const iccidColIdx = header.findIndex(cell => /iccid/i.test(cell));
  if (iccidColIdx >= 0) {
    return rows
      .slice(1)
      .map(row => row[iccidColIdx] || '')
      .filter(Boolean);
  }
  // No header: every row's first column.
  return rows.map(row => row[0] || '').filter(Boolean);
};

const FirmwareUpgradeBulkPage = props => {
  const navigate = useNavigate();
  const [version, setVersion] = useState(null);
  const [rawIccids, setRawIccids] = useState('');
  const [upgradeType, setUpgradeType] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [preflighting, setPreflighting] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);
  const [error, setError] = useState(null);
  const [csvError, setCsvError] = useState(null);

  const unitModels = useSelector(selectUnitModels);
  const resolveModelName = useMemo(() => buildModelNameResolver(unitModels), [unitModels]);

  const iccids = useMemo(() => Array.from(new Set(splitIccids(rawIccids))), [rawIccids]);
  const overLimit = iccids.length > MAX_ICCIDS;
  const canSubmit = version && iccids.length > 0 && !overLimit && !submitting && !preflighting;

  const handleCsvFile = async event => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setCsvError(null);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length === 0) {
        setCsvError('No ICCIDs found in CSV.');
        return;
      }
      // Append to (rather than replace) the textarea so the user can
      // mix paste + CSV without losing what they already entered.
      setRawIccids(prev => (prev.trim() ? `${prev.trim()}\n${parsed.join('\n')}` : parsed.join('\n')));
    } catch (e) {
      setCsvError(e.message || 'Failed to read CSV.');
    }
  };

  // Submit a specific subset of iccids — used both by the all-clean
  // bypass and by "proceed" in the dialog after the user picks which
  // warning rows to include.
  const startBatch = async iccidsToSend => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await startFirmwareUpgradeBulk({
        firmwareVersionId: version.id,
        iccids: iccidsToSend,
        upgradeType,
        notes,
      });
      const batchId = response.data?.batch?.id;
      if (batchId) {
        navigate(`/staff/firmware-upgrades/${batchId}`);
      }
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 400 && data?.code === 'mixed_upgrade_batch') {
        setError(formatMixedBatchError(data));
      } else if (err.response?.status === 400 && data?.code === 'incompatible_firmware_model') {
        setError(formatIncompatibleFirmwareModelError(data, resolveModelName));
      } else {
        setError(data || err.message || 'Failed to start upgrade');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setPreflighting(true);
    setError(null);
    try {
      const response = await preflightFirmwareUpgradeBulk({
        firmwareVersionId: version.id,
        iccids,
        upgradeType,
      });
      const result = response.data;
      if (preflightHasWarnings(result.summary)) {
        // Open the confirm dialog so the user picks which warning rows to keep.
        setPreflightResult(result);
      } else {
        // All clean — skip the dialog and go straight to launch.
        await startBatch(iccids);
      }
    } catch (err) {
      setError(err.response?.data || err.message || 'Pre-flight check failed');
    } finally {
      setPreflighting(false);
    }
  };

  const proceed = async selectedIccids => {
    setPreflightResult(null);
    await startBatch(selectedIccids);
  };

  return (
    <AppNav page="admin_firmware_upgrades_new" notFullHeight {...props}>
      <Box sx={{ p: 2, maxWidth: 760 }}>
        <TitleBar title="New Firmware Upgrade" />
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <FirmwareVersionAutocomplete value={version} onChange={setVersion} />

              <FormControl size="small">
                <InputLabel>Upgrade type</InputLabel>
                <Select value={upgradeType} label="Upgrade type" onChange={e => setUpgradeType(e.target.value)}>
                  {UPGRADE_TYPES.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Auto picks the right upgrader for each unit&apos;s model. Choose a specific type only to override.
                </FormHelperText>
              </FormControl>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileIcon fontSize="small" />}
                >
                  Upload CSV
                  <input type="file" accept=".csv,text/csv,text/plain" hidden onChange={handleCsvFile} />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Header row auto-detected if any column header contains &quot;ICCID&quot;.
                </Typography>
              </Stack>
              {csvError && (
                <Alert severity="warning" onClose={() => setCsvError(null)}>
                  {csvError}
                </Alert>
              )}

              <TextField
                label="ICCIDs (one per line, or comma-separated)"
                multiline
                minRows={6}
                value={rawIccids}
                onChange={e => setRawIccids(e.target.value)}
                helperText={`${iccids.length} unique identifier${iccids.length === 1 ? '' : 's'} parsed${
                  overLimit ? ` — exceeds ${MAX_ICCIDS} limit` : ''
                }`}
                error={overLimit}
                // Anchor pasted text to the top of the tall filled slot instead of
                // MUI's default vertical centering (see SettingsTextField.jsx).
                sx={{
                  '& .MuiInputBase-root.MuiInputBase-multiline': { alignItems: 'flex-start' },
                  '& .MuiInputBase-inputMultiline': { alignSelf: 'flex-start', padding: '0 !important' },
                }}
              />

              <TextField label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} size="small" />

              {error && (
                <Alert severity="error">
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {typeof error === 'string' ? error : JSON.stringify(error, null, 2)}
                  </Typography>
                </Alert>
              )}

              <Box>
                <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
                  {preflighting ? 'Checking…' : submitting ? 'Starting…' : 'Start upgrade'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <PreflightWarningsDialog
        result={preflightResult}
        onCancel={() => setPreflightResult(null)}
        onConfirm={proceed}
        submitting={submitting}
      />
    </AppNav>
  );
};

const preflightSecondaryLine = (entry, target) => {
  if (entry.status === 'match' && entry.current_firmware) {
    return `Currently on v${entry.current_firmware.version}.`;
  }
  if (entry.status === 'model_mismatch') {
    const model = entry.unit_model ? `a ${entry.unit_model}` : 'a different hardware model';
    return `Unit is ${model} — this firmware was not built for it and cannot be included.`;
  }
  if (entry.status === 'mismatch' && entry.current_firmware) {
    return `Currently on v${entry.current_firmware.version} (major ${entry.current_firmware.major}); target major ${target.major}.`;
  }
  if (entry.status === 'unknown_firmware') {
    return 'Unit exists but has never reported firmware.';
  }
  if (entry.status === 'no_ip') {
    return 'Radio has no IP address — cannot reach the unit for this upgrade type. Switch to PERSISTENT or skip.';
  }
  if (entry.status === 'unresolved') {
    return 'No SmartLinkUnit matches this identifier — cannot be included.';
  }
  if (entry.status === 'ambiguous') {
    return `Matches multiple units (ids: ${(entry.unit_ids || []).join(', ')}) — cannot be included.`;
  }
  return null;
};

const PreflightWarningsDialog = ({ result, onCancel, onConfirm, submitting }) => {
  // Track which ICCIDs the user has opted to include. Initialized when
  // the dialog opens: `match` rows checked, `mismatch` / `unknown_firmware`
  // unchecked, `unresolved` / `ambiguous` never checkable (no unit to
  // act on). The user can either tick individual risky rows or use the
  // "Include all warnings" quick action.
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    setSelected(initialPreflightSelection(result?.entries));
  }, [result]);

  if (!result) return null;
  const { summary, entries, target_firmware: target } = result;

  const toggleIccid = iccid => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(iccid)) next.delete(iccid);
      else next.add(iccid);
      return next;
    });
  };

  const includesAnyRisk = entries.some(e => RISKY_STATUSES.has(e.status) && selected.has(e.iccid));
  const riskyAllIn = allRiskySelected(entries, selected);
  const modelMismatchCount = countByStatus(entries, 'model_mismatch');
  // Blocked rows can't be ticked, so this only fires if a future edit makes
  // them selectable. It is the last gate before a bricking upgrade goes out.
  const blocked = selectionHasBlockedEntry(entries, selected);

  const setRiskyInclusion = include => setSelected(prev => applyRiskyInclusion(prev, entries, include));

  const selectableCount = entries.filter(e => SELECTABLE_STATUSES.has(e.status)).length;
  const confirmLabel = `Start upgrade for ${selected.size}/${selectableCount}`;
  const confirmDisabled = submitting || selected.size === 0 || blocked;
  const confirmColor = includesAnyRisk ? 'error' : 'primary';

  return (
    <Dialog open onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningIcon color="warning" />
          <span>Confirm firmware upgrade</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Targeting <strong>v{target.version}</strong> (major {target.major ?? '—'}). Match units are checked by
          default; tick risky rows individually or use the quick action below to include them all.
        </DialogContentText>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip size="small" color="success" label={`Match: ${summary.match}`} variant="outlined" />
          {summary.model_mismatch > 0 && (
            <Chip size="small" color="error" label={`Model mismatch: ${summary.model_mismatch}`} variant="outlined" />
          )}
          {summary.mismatch > 0 && (
            <Chip size="small" color="warning" label={`Major mismatch: ${summary.mismatch}`} variant="outlined" />
          )}
          {summary.unknown_firmware > 0 && (
            <Chip
              size="small"
              color="info"
              label={`Unknown firmware: ${summary.unknown_firmware}`}
              variant="outlined"
            />
          )}
          {summary.no_ip > 0 && (
            <Chip size="small" color="error" label={`No IP: ${summary.no_ip}`} variant="outlined" />
          )}
          {summary.unresolved > 0 && (
            <Chip size="small" color="error" label={`Unresolved: ${summary.unresolved}`} variant="outlined" />
          )}
          {summary.ambiguous > 0 && (
            <Chip size="small" color="error" label={`Ambiguous: ${summary.ambiguous}`} variant="outlined" />
          )}
        </Stack>

        {(summary.mismatch > 0 || summary.unknown_firmware > 0) && (
          <Box sx={{ mb: 1 }}>
            <Button
              size="small"
              color={riskyAllIn ? 'inherit' : 'warning'}
              variant="text"
              onClick={() => setRiskyInclusion(!riskyAllIn)}
            >
              {riskyAllIn ? 'Exclude all warnings' : 'Include all warnings (dangerous)'}
            </Button>
          </Box>
        )}

        {modelMismatchCount > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {modelMismatchCount} unit{modelMismatchCount === 1 ? ' is' : 's are'} a different hardware model than this
            firmware was built for. Model mismatches are excluded from the batch and from &quot;include all
            warnings&quot; — use an image built for their model instead.
          </Alert>
        )}

        <Box
          sx={{
            maxHeight: 360,
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <List dense disablePadding>
            {entries.map(entry => {
              const selectable = SELECTABLE_STATUSES.has(entry.status);
              const checked = selectable && selected.has(entry.iccid);
              return (
                <ListItem
                  key={entry.iccid}
                  divider
                  sx={{ alignItems: 'flex-start', opacity: selectable ? 1 : 0.55 }}
                  onClick={selectable && !submitting ? () => toggleIccid(entry.iccid) : undefined}
                  button={selectable && !submitting}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={checked}
                      disabled={!selectable || submitting}
                      tabIndex={-1}
                      disableRipple
                      sx={{ p: 0.5 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {entry.iccid}
                        </Typography>
                        <PreflightStatusChip status={entry.status} />
                      </Stack>
                    }
                    secondary={preflightSecondaryLine(entry, target)}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        {includesAnyRisk && (
          <Alert severity="error" sx={{ mt: 2 }}>
            You&apos;ve included one or more units with major-version mismatches or unknown firmware. These upgrades may
            brick the device — proceed only if you&apos;ve verified compatibility out-of-band.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(Array.from(selected))}
          color={confirmColor}
          variant="contained"
          disabled={confirmDisabled}
        >
          {submitting ? 'Starting…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PreflightStatusChip = ({ status }) => {
  const config = {
    model_mismatch: { color: 'bad', label: 'Model mismatch' },
    mismatch: { color: 'warning', label: 'Major mismatch' },
    unknown_firmware: { color: 'info', label: 'Unknown firmware' },
    no_ip: { color: 'bad', label: 'No radio IP' },
    unresolved: { color: 'bad', label: 'Not found' },
    ambiguous: { color: 'bad', label: 'Ambiguous' },
    match: { color: 'ok', label: 'Match' },
  }[status] || { color: 'neutral', label: status };
  return <StatusChip color={config.color} label={config.label} />;
};

export default FirmwareUpgradeBulkPage;
